import PropTypes from "prop-types";
import { forwardRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

// material-ui
import { useTheme } from "@mui/material/styles";
import {
  Avatar,
  Chip,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
} from "@mui/material";

// project imports
import useConfig from "hooks/useConfig";

// assets
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { useStateValue } from "store";
import usePermissions from "hooks/usePermissions";

// ==============================|| SIDEBAR MENU LIST ITEMS ||============================== //

const NavItem = ({ item, level }) => {
  const [{ menu }, dispatch] = useStateValue();
  const { userPerms } = usePermissions();
  const selectedItem = menu.selectedItem;
  const theme = useTheme();
  const { pathname } = useLocation();
  const Icon = item?.icon;
  const itemIcon = item?.icon ? (
    <Icon stroke={1} size="24px" />
  ) : (
    <FiberManualRecordIcon
      sx={{
        width: selectedItem?.findIndex((id) => id === item?.id) > -1 ? 8 : 6,
        height: selectedItem?.findIndex((id) => id === item?.id) > -1 ? 8 : 6,
      }}
      fontSize={level > 0 ? "inherit" : "medium"}
    />
  );

  let itemTarget = "_self";
  if (item.target) {
    itemTarget = "_blank";
  }

  let listItemProps = {
    component: forwardRef((props, ref) => (
      <Link ref={ref} {...props} to={item.url} target={itemTarget} />
    )),
  };
  if (item?.external) {
    listItemProps = { component: "a", href: item.url, target: itemTarget };
  }

  const itemHandler = (id) => {
    dispatch({ type: "SET_ACTIVE_ITEM", payload: [id] });
    switch (id) {
      case "schedule":
      case "dashboard":
      case "reports":
      case "production":
      case "messaging":
      case "leaderboard":
      case "terminal":
      case "appointments":
        dispatch({ type: "OPEN_DRAWER", payload: false });
        break;
    }
  };

  const disabledByPermissions = (id) => {
    switch (id) {
      case "schedule":
        return userPerms.scheduler === 1;
      case "dashboard":
      case "reports":
        return userPerms.dash === 1;
      case "location-settings":
        return userPerms.admin === 1;
      case "messaging":
        return userPerms.messaging === 1;
      default:
        return false;
    }
  };

  // active menu item on page load
  useEffect(() => {
    const currentIndex = document.location.pathname
      .toString()
      .split("/")
      .findIndex((id) => id === item.id);
    if (currentIndex > -1) {
      dispatch({ type: "SET_ACTIVE_ITEM", payload: [item.id] });
    }
  }, [pathname]);

  return (
    <ListItemButton
      {...listItemProps}
      disabled={disabledByPermissions(item.id)}
      sx={{
        borderRadius: `27px`,
        mb: 0.5,
        alignItems: "flex-start",
        backgroundColor: level > 1 ? "transparent !important" : "inherit",
        py: level > 1 ? 1.25 : 1,
        pl: `${level * 16}px`,
      }}
      selected={selectedItem?.findIndex((id) => id === item.id) > -1}
      onClick={() => itemHandler(item.id)}
    >
      <ListItemIcon sx={{ my: "auto", minWidth: !item?.icon ? 16 : 32 }}>
        {itemIcon}
      </ListItemIcon>
      <ListItemText
        primary={
          <Typography
            variant={
              selectedItem?.findIndex((id) => id === item.id) > -1
                ? "h5"
                : "body1"
            }
            color="inherit"
          >
            {item.title}
          </Typography>
        }
        secondary={
          item.caption && (
            <Typography
              variant="caption"
              sx={{ ...theme.typography.subMenuCaption }}
              display="block"
              gutterBottom
            >
              {item.caption}
            </Typography>
          )
        }
      />
      {item.chip && (
        <Chip
          color={item.chip.color}
          variant={item.chip.variant}
          size={item.chip.size}
          label={item.chip.label}
          avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
        />
      )}
    </ListItemButton>
  );
};

NavItem.propTypes = {
  item: PropTypes.object,
  level: PropTypes.number,
};

export default NavItem;
